import React from "react"

import { useAuth } from "@/contexts/AuthContext"
import { Navigate, useLocation, useParams } from "react-router-dom"

import { cn } from "@/lib/utils"
import logo from '@/assets/logo.png'

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { CircleCheck, InfoIcon, LoaderCircleIcon, ShieldAlertIcon } from "lucide-react"
import { supabase } from "@/lib/supabase"
import clsx from "clsx"

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> { }



export default function Signup() {
  const { session } = useAuth()
  const location = useLocation()

  if (session) {
    return <Navigate to={location.state?.pathname || '/orders'} />
  }

  return (
    <>
      <div className="w-screen relative flex h-screen flex-col items-center justify-center items-center">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px] relative bottom-16">
          <div className="flex flex-col space-y-1 text-center">
            <div className="relative z-20 flex items-center text-center justify-center text-lg font-medium gap-2 flex-col">
              <img src={logo} className="h-8 w-8" />
              Universus
            </div>
            <p className="text-sm text-muted-foreground">
              Definir un mot de passe
            </p>
          </div>
          <SignupForm />
        </div>
      </div>
    </>
  )
}

export function SignupForm({ className, ...props }: UserAuthFormProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [password, setPassword] = React.useState<string>('')
  const [password2, setPassword2] = React.useState<string>('')
  const [error, setError] = React.useState<string>(false)
  const { token } = useParams()

  const isValid =
    isPasswordValid(password) &&
    password === password2

  async function onFinish(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid) return

    setIsLoading(true)

    const { data, error } = await supabase.functions.invoke('connect-user', {
      body: {
        token,
        password
      }
    })

    if (!data || data.error || error) {
      setIsLoading(false)
      setError(true)
    } else {
      window.location.href = data.magicLink
    }
  }

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <form onSubmit={onFinish}>
        <div className="grid gap-4">

          <div className="text-xs text-muted-foreground space-y-1">
            <p className={clsx("flex items-center gap-1.5", password.length >= 8 && "text-green-500")}>
              {password.length >= 8 ? <CircleCheck className="w-4 h-4" /> : <InfoIcon className="w-4 h-4" />}
              Au moins 8 caractères
            </p>
            <p className={clsx("flex items-center gap-1.5", /[a-z]/.test(password) && "text-green-500")}>
              {/[a-z]/.test(password) ? <CircleCheck className="w-4 h-4" /> : <InfoIcon className="w-4 h-4" />}
              Une lettre minuscule
            </p>
            <p className={clsx("flex items-center gap-1.5", /[A-Z]/.test(password) && "text-green-500")}>
              {/[A-Z]/.test(password) ? <CircleCheck className="w-4 h-4" /> : <InfoIcon className="w-4 h-4" />}
              Une lettre majuscule
            </p>
            <p className={clsx("flex items-center gap-1.5", /\d/.test(password) && "text-green-500")}>
              {/\d/.test(password) ? <CircleCheck className="w-4 h-4" /> : <InfoIcon className="w-4 h-4" />}
              Un chiffre
            </p>
            <p className={clsx("flex items-center gap-1.5", /[^A-Za-z0-9]/.test(password) && "text-green-500")}>
              {/[^A-Za-z0-9]/.test(password) ? <CircleCheck className="w-4 h-4" /> : <InfoIcon className="w-4 h-4" />}
              Un symbole (ex: @ ! ? #)
            </p>
            <p className={clsx("flex items-center gap-1.5", password === password2 && password.length > 0 && "text-green-500")}>
              {password === password2 && password.length > 0 ? <CircleCheck className="w-4 h-4" /> : <InfoIcon className="w-4 h-4" />}
              Les mots de passe doivent être identiques.
            </p>
          </div>

          <div className="grid gap-1">
            <Input
              type="password"
              placeholder="Nouveau mot de passe"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
              }}
            />
          </div>

          <div className="grid gap-1">
            <Input
              type="password"
              placeholder="Confirmer le mot de passe"
              autoComplete="new-password"
              value={password2}
              onChange={(event) => setPassword2(event.target.value)}
            />
          </div>

          <Button type="submit" disabled={isLoading || !isValid}>
            {isLoading && (
              <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Se connecter
          </Button>

          <div className="min-h-[1.5rem] flex items-center justify-center text-xs text-red-500 transition-opacity duration-300">
            {error && (
              <>
                <ShieldAlertIcon className="w-4 h-4 mr-2" />
                Le lien a expiré ou a déjà été utilisé.
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  )
}

function isPasswordValid(password: string): boolean {
  const lengthOK = password.length >= 8
  const hasLower = /[a-z]/.test(password)
  const hasUpper = /[A-Z]/.test(password)
  const hasDigit = /\d/.test(password)
  const hasSymbol = /[^A-Za-z0-9]/.test(password)

  return lengthOK && hasLower && hasUpper && hasDigit && hasSymbol
}